import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CategoryColorsState {
  productCategoryColor: { [key: string]: string };
  colorTone: number;
}

const initialState: CategoryColorsState = {
  productCategoryColor: {},
  colorTone: 50
};

// Available color palettes from global.css
const colorPalettes = [
  'primary',
  'neutral', 
  'success',
  'warning',
  'danger',
  'info',
  'general',
  'custom-app',
  'other',
  'finance',
  'recruitment',
  'marketing',
  'sales',
  'time-planning'
];

// Function to get a random color from the selected tone
const getRandomColorFromTone = (tone: number): string => {
  const randomPalette = colorPalettes[Math.floor(Math.random() * colorPalettes.length)];
  return `var(--${randomPalette}-${tone})`;
};

// Function to check if a color is dark
const isDarkColor = (hex: string): boolean => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse r, g, b values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calculate luminance
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  
  // Return true if dark (threshold is 128)
  return luminance < 128;
};

export const categoryColorsSlice = createSlice({
  name: 'categoryColors',
  initialState,
  reducers: {
    setColorTone: (state, action: PayloadAction<number>) => {
      state.colorTone = action.payload;
    },
    setProductCategoryColor: (state, action: PayloadAction<{ categoryName: string; color?: string }>) => {
      const { categoryName, color } = action.payload;
      if (color) {
        state.productCategoryColor[categoryName] = color;
      } else {
        // Generate a random color if not provided
        state.productCategoryColor[categoryName] = getRandomColorFromTone(state.colorTone);
      }
    },
    clearCategoryColors: (state) => {
      state.productCategoryColor = {};
    },
    removeCategoryColor: (state, action: PayloadAction<string>) => {
      delete state.productCategoryColor[action.payload];
    }
  }
});

// Thunk for getting or setting a category color
export const getProductCategoryColor = (categoryName: string) => (dispatch: any, getState: any) => {
  const state = getState();
  const { productCategoryColor } = state.categoryColors;
  
  if (productCategoryColor[categoryName]) {
    return productCategoryColor[categoryName];
  } else {
    // If color doesn't exist, set it and return the new color
    const newColor = getRandomColorFromTone(state.categoryColors.colorTone);
    dispatch(setProductCategoryColor({ categoryName, color: newColor }));
    return newColor;
  }
};

// Helper function to get text color based on background color
export const getTextColorForBackground = (backgroundColor: string): string => {
  // For CSS variables, we'll use a default approach
  // Since we're using CSS variables from global.css, we can make educated guesses
  if (backgroundColor.startsWith('var(--')) {
    // Extract the palette and tone from the CSS variable
    const match = backgroundColor.match(/var\(--([^-]+)-(\d+)\)/);
    if (match) {
      const [, palette, tone] = match;
      const toneNum = parseInt(tone);
      
      // For lighter tones (0-30), use dark text
      // For darker tones (70-90), use light text
      // For medium tones (40-60), use dark text for most palettes
      if (toneNum <= 30) {
        return '#000'; // Dark text for light backgrounds
      } else if (toneNum >= 70) {
        return '#fff'; // Light text for dark backgrounds
      } else {
        // For medium tones, use dark text for most palettes
        return '#000';
      }
    }
    return '#000'; // Default fallback
  }
  
  if (backgroundColor.startsWith('#')) {
    return isDarkColor(backgroundColor) ? '#fff' : '#000';
  }
  
  return '#000'; // Default fallback
};

export const { 
  setColorTone, 
  setProductCategoryColor, 
  clearCategoryColors, 
  removeCategoryColor 
} = categoryColorsSlice.actions;

export default categoryColorsSlice.reducer; 